import {Amplify} from "aws-amplify";
import {useContext} from "react";
import {AuthContextType, createAuthContext} from "./context/Auth.ts";
import {AuthProvider, createAuthProvider, UserAttributeMappers} from "./providers/AuthProvider.tsx";
import {fetchAuthSession} from "@aws-amplify/auth";

export type {JWTPayload, UpdatedUserAttributes} from "./providers/AuthProvider.tsx"
export type {AuthState} from "./context/Auth.ts"
export * from "./constants/auth.ts"
export * from "./lib/errors.ts"

export type AuthModuleConfig<UserAttributes> = UserAttributeMappers<UserAttributes> & {
    cognito: {
        userPoolClientId: string
        userPoolId: string
        userPoolEndpoint: string
        oauthDomain: string
    }
}

export type TokenRetriever = () => Promise<string>

type AuthModule<UserAttributes> = {
    Provider: AuthProvider,
    useAuth: () => AuthContextType<UserAttributes>
    tokenRetriever: TokenRetriever
}

export const createAuthModule = <UserAttributes>(conf: AuthModuleConfig<UserAttributes>): AuthModule<UserAttributes> => {
    const {cognito, ...mappers} = conf

    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolClientId: conf.cognito.userPoolClientId,
                userPoolId: conf.cognito.userPoolId,
                userPoolEndpoint: conf.cognito.userPoolEndpoint,
                loginWith: {
                    email: true,
                    oauth: {
                        domain: conf.cognito.oauthDomain,
                        scopes: ["email", "openid"],
                        redirectSignIn: [`${window.location.origin}/login`],
                        redirectSignOut: [`${window.location.origin}/logout`],
                        responseType: 'code',
                    }
                },
            }
        }
    });

    const AuthContext = createAuthContext<UserAttributes>()
    const AuthProvider = createAuthProvider<UserAttributes>({
        authContext: AuthContext,
        ...mappers,
    })
    const useAuth = (): AuthContextType<UserAttributes> => useContext(AuthContext)

    return {
        Provider: AuthProvider,
        useAuth,
        tokenRetriever: async () => {
            const sess = await fetchAuthSession()
            if (sess.tokens) {
                return (sess.tokens.idToken ?? sess.tokens.accessToken).toString()
            }
            throw "no tokens found in session"
        }
    }
}
